import { useState, useEffect, type FormEvent } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

// Basic RFC-5322-lite shape check — just enough to catch obvious typos
// before we hit the server. The real validation lives in the backend's
// EmailStr validator (F15).
function isLikelyEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

// The /auth/forgot-password endpoint returns 200 regardless of whether the
// email is registered — this prevents account enumeration. The UI mirrors
// that behaviour: we always show the same success screen after submission.
export default function ForgotPassword() {
  useEffect(() => {
    document.title = "Forgot Password — PREP POS";
  }, []);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Enter your email.");
      return;
    }
    if (!isLikelyEmail(trimmed)) {
      setEmailError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/auth/forgot-password", { email: trimmed });
    } catch {
      // Swallow errors so the response is indistinguishable from the
      // happy path — matches the server's enumeration-resistant contract.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-serif">
            Forgot your password?
          </h1>
          <p className="text-muted-foreground">
            Enter your email and we&apos;ll send you a reset link.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-6">
          {submitted ? (
            <div className="space-y-4 text-center">
              <p className="text-foreground">
                If an account exists for that email, a reset link is on its way.
              </p>
              <p className="text-sm text-muted-foreground">
                The link expires in 60 minutes. Check your spam folder if you
                don&apos;t see it shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? "fp-email-error" : undefined}
                  className="h-11"
                />
                {emailError ? (
                  <p
                    id="fp-email-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {emailError}
                  </p>
                ) : null}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base bg-primary hover:bg-primary/90"
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                Send reset link
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="font-medium text-primary hover:underline">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
